import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './TermsAndConditionsModal.module.css';
import { TermsContent } from './termsContent';

type TermsAndConditionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const TermsAndConditionsModal = ({ isOpen, onClose }: TermsAndConditionsModalProps) => {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 id="terms-modal-title" className={styles.title}>
            Terms & Conditions
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className={styles.content}>
          <div className={styles.scrollWrapper}>
            <div className={styles.scrollArea}>
              <TermsContent />
            </div>
            <div className={styles.gradientOverlay} aria-hidden />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
